import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/formatDate';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import {
  acknowledgeOutside, listAccessRequests, listAllVendors, listOf, listProposals, MAX_ROWS,
} from './vendorsApi';
import {
  REVIEW_STATUS_LABEL, VENDOR_ERRORS, atLeast, levelForTags,
} from './vendorLabels';
import { Skeleton, FullError } from './VendorBits';
import { AccessRequestCard, ProposalCard } from './ReviewQueueCards';
import MarkReviewedDialog from './MarkReviewedDialog';

const EMPTY = { proposals: [], requests: [], vendors: [], failed: false, capped: false };

const byId = (rows) => [...new Map(rows.map((r) => [r.id, r])).values()];

/**
 * "To review": what this person (as a speciality owner or admin) must act on.
 * Each group renders nothing when empty. A source that failed to load is shown
 * as a failure, never as an empty group or the all-clear message.
 */
export default function ReviewQueue({ me, onChanged }) {
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(EMPTY);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(() => {
    setStatus('loading');
    // Outside changes (outside=1) and reviews due (overdue + due_soon) are
    // vendor queries, each paged to completion up to MAX_ROWS.
    Promise.allSettled([
      listProposals('review'),
      listAccessRequests('review'),
      listAllVendors({ outside: 1 }),
      listAllVendors({ review: 'overdue' }),
      listAllVendors({ review: 'due_soon' }),
    ]).then(([p, r, o, od, ds]) => {
      const vendorSets = [o, od, ds].filter((x) => x.status === 'fulfilled').map((x) => x.value);
      setData({
        proposals: p.status === 'fulfilled' ? listOf(p.value, 'proposals') : [],
        requests: r.status === 'fulfilled' ? listOf(r.value, 'requests') : [],
        vendors: byId(vendorSets.flatMap((v) => v.rows)),
        capped: vendorSets.some((v) => v.capped),
        failed: [p, r, o, od, ds].some((x) => x.status === 'rejected'),
      });
      setStatus('ready');
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const dropFrom = (key, id) => {
    setData((d) => ({ ...d, [key]: d[key].filter((x) => x.id !== id) }));
    if (onChanged) onChanged();
  };

  // A vendor can sit in both vendor groups, so resolving one group clears only
  // its own flag instead of dropping the vendor from the other group too.
  const patchVendor = (id, patch) => {
    setData((d) => ({ ...d, vendors: d.vendors.map((v) => (v.id === id ? { ...v, ...patch } : v)) }));
    if (onChanged) onChanged();
  };

  const acknowledge = async (vendor) => {
    try {
      await acknowledgeOutside(vendor.id);
      toastSuccess('Acknowledged.');
      patchVendor(vendor.id, { outside_change_since: null });
    } catch (err) {
      toastError(errorMessage(err, 'Could not acknowledge the change.', VENDOR_ERRORS));
      // Already cleared by someone else: the card is stale, so drop it.
      if (err?.response?.status === 409 || err?.response?.data?.error === 'nothing_to_acknowledge') {
        patchVendor(vendor.id, { outside_change_since: null });
      }
    }
  };

  if (status === 'loading') return <Skeleton label="Loading your review queue" />;

  // Only vendors this person approves, by the same rule the server applies.
  const mine = data.vendors.filter((v) => atLeast(levelForTags(me, (v.specialities || []).map((s) => s.id)), 'approve'));
  const outside = mine.filter((v) => v.outside_change_since && v.active !== false);
  const due = mine.filter((v) => v.active !== false && (v.review_status === 'overdue' || v.review_status === 'due_soon'));
  const total = data.proposals.length + data.requests.length + outside.length + due.length;

  return (
    <div>
      {data.capped && <p className="vd-list-meta">Showing the first {MAX_ROWS} vendors.</p>}
      {data.failed && <FullError message="Couldn't load everything waiting for you. Try again in a minute." onRetry={load} />}
      {!data.failed && total === 0 && <div className="vd-empty"><p>Nothing waiting for you.</p></div>}

      {data.proposals.length > 0 && (
        <section aria-labelledby="vd-q-proposals">
          <h2 id="vd-q-proposals" className="vd-group-head">Proposals</h2>
          <ul className="vd-list">
            {data.proposals.map((p) => <ProposalCard key={p.id} proposal={p} onGone={() => dropFrom('proposals', p.id)} />)}
          </ul>
        </section>
      )}

      {data.requests.length > 0 && (
        <section aria-labelledby="vd-q-requests">
          <h2 id="vd-q-requests" className="vd-group-head">Access requests</h2>
          <ul className="vd-list">
            {data.requests.map((r) => <AccessRequestCard key={r.id} request={r} onGone={() => dropFrom('requests', r.id)} />)}
          </ul>
        </section>
      )}

      {outside.length > 0 && (
        <section aria-labelledby="vd-q-outside">
          <h2 id="vd-q-outside" className="vd-group-head">Changed outside Nexus</h2>
          <ul className="vd-list">
            {outside.map((v) => (
              <li key={v.id} className="vd-card">
                <div className="vd-card-head">
                  <Link to={`/vendors/${v.id}`}>{v.name}</Link>
                  <span className="vd-list-meta">Changed in Odoo on {formatDate(v.outside_change_since)}</span>
                </div>
                <div className="vd-card-actions">
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => acknowledge(v)}>Acknowledge</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {due.length > 0 && (
        <section aria-labelledby="vd-q-due">
          <h2 id="vd-q-due" className="vd-group-head">Reviews due</h2>
          <ul className="vd-list">
            {due.map((v) => (
              <li key={v.id} className="vd-card">
                <div className="vd-card-head">
                  <Link to={`/vendors/${v.id}`}>{v.name}</Link>
                  <span className="vd-chip vd-chip--flag">{REVIEW_STATUS_LABEL[v.review_status]}</span>
                  <span className="vd-list-meta">{formatDate(v.effective_next_review || v.next_review)}</span>
                </div>
                <div className="vd-card-actions">
                  <button type="button" className="btn btn-primary btn-small" onClick={() => setReviewing(v)}>Mark reviewed</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {reviewing && (
        <MarkReviewedDialog
          vendor={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => { patchVendor(reviewing.id, { review_status: 'ok' }); setReviewing(null); }}
        />
      )}
    </div>
  );
}
